import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { cn } from '../../lib/utils';
import { Image, Film, Music, FileText, FolderOpen, Download, Cloud, Trash2, RefreshCw, Clock, Eye, Play, Store } from 'lucide-react';
import { downloadFile } from './shared';
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export function LibraryView({ items, filter, setFilter, driveConnected, onSaveDrive, onDelete, onPreview, loading }) {
  const filtered = filter === 'all' ? items : items.filter(i => i.type === filter);
  const FILTERS = [{ key: 'all', label: 'All', icon: FolderOpen }, { key: 'image', label: 'Images', icon: Image }, { key: 'video', label: 'Videos', icon: Film }, { key: 'music', label: 'Music', icon: Music }, { key: 'text', label: 'Text', icon: FileText }];
  if (loading) return <div className="flex justify-center p-8"><RefreshCw className="h-5 w-5 animate-spin text-gray-400" /></div>;
  return (
    <div className="space-y-4" data-testid="content-library">
      <div className="flex gap-2 flex-wrap">{FILTERS.map(f => (
        <Button key={f.key} variant={filter === f.key ? 'default' : 'outline'} size="sm" className="text-xs gap-1.5" onClick={() => setFilter(f.key)} data-testid={`filter-${f.key}`}><f.icon className="h-3 w-3" />{f.label}</Button>
      ))}</div>
      {!driveConnected && <Card className="bg-blue-50 border-blue-200"><CardContent className="p-3 flex items-center gap-3"><Cloud className="h-5 w-5 text-blue-500 flex-shrink-0" /><div><p className="text-xs font-medium text-blue-800">Connect Google Drive to keep your content forever</p><p className="text-[10px] text-blue-600">Without Drive, items auto-delete after 30 days.</p></div></CardContent></Card>}
      {filtered.length === 0 ? (
        <Card className="bg-white border-gray-border"><CardContent className="p-12 text-center"><FolderOpen className="h-12 w-12 mx-auto mb-3 text-gray-200" /><p className="text-sm text-gray-medium">No content yet</p></CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {filtered.map(item => <LibraryCard key={item.item_id} item={item} driveConnected={driveConnected} onSaveDrive={onSaveDrive} onDelete={onDelete} onPreview={onPreview} />)}
        </div>
      )}
    </div>
  );
}

function LibraryCard({ item, driveConnected, onSaveDrive, onDelete, onPreview }) {
  const typeColors = { image: 'bg-violet-100 text-violet-700', video: 'bg-blue-100 text-blue-700', music: 'bg-pink-100 text-pink-700', text: 'bg-emerald-100 text-emerald-700' };
  const daysLeft = item.days_remaining ?? 30;
  const [listing, setListing] = useState(false);

  const handleSell = async () => {
    const priceStr = window.prompt(`Price this item for the Konnect Marketplace (USD):\n\n"${item.title}"`, '9.99');
    if (!priceStr) return;
    const price = parseFloat(priceStr);
    if (!price || price <= 0) { toast.error('Enter a valid price greater than 0'); return; }
    if (!item.url) { toast.error('No downloadable file attached to this item yet.'); return; }
    setListing(true);
    try {
      const res = await axios.post(`${API}/api/marketplace/listings/digital-download`, {
        name: item.title || `Konnect ${item.type} — digital download`,
        description: (item.content || '').slice(0, 500) || `Generated with KoolKonnect. Instant download after purchase.`,
        price,
        currency: 'USD',
        category: 'digital',
        tags: [item.type, 'konnect'],
        image_urls: item.type === 'image' && item.url ? [item.url] : [],
        preview_url: item.type === 'image' ? item.url : '',
        download_source_url: item.url.startsWith('http') ? item.url : `${API}${item.url}`,
        file_type: item.type,
        asset_id: String(item.item_id || ''),
      });
      if (res.data?.success) {
        toast.success(`Listed on Marketplace ✓ ($${price})`);
      } else {
        toast.error(res.data?.error || 'Could not create listing');
      }
    } catch (e) {
      toast.error('Failed to create marketplace listing');
    } finally {
      setListing(false);
    }
  };
  return (
    <Card className="bg-white border-gray-border hover:shadow-md transition-all group" data-testid={`library-item-${item.item_id}`}>
      <div className="aspect-video bg-gray-50 relative overflow-hidden cursor-pointer" onClick={() => onPreview(item)}>
        {item.type === 'image' && item.url ? <img src={item.url} alt="" className="w-full h-full object-cover" loading="lazy" /> :
         item.type === 'video' ? <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50"><Play className="h-10 w-10 text-blue-400" /></div> :
         item.type === 'music' ? <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-pink-50 to-purple-50"><Music className="h-10 w-10 text-pink-400" /></div> :
         <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-emerald-50 to-teal-50 p-3"><p className="text-[10px] text-gray-500 line-clamp-4">{item.content?.substring(0, 120) || item.title}</p></div>}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center"><Eye className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" /></div>
        <Badge className={cn("absolute top-2 left-2 text-[9px]", typeColors[item.type])}>{item.type}</Badge>
        {item.saved_to_drive && <Cloud className="absolute top-2 right-2 h-3.5 w-3.5 text-teal-500" />}
      </div>
      <CardContent className="p-2.5">
        <p className="text-xs font-medium text-text-primary truncate mb-1">{item.title}</p>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1"><Clock className={cn("h-3 w-3", daysLeft <= 3 ? "text-red-500" : "text-gray-400")} /><span className={cn("text-[10px]", daysLeft <= 3 ? "text-red-500 font-medium" : "text-gray-400")}>{daysLeft}d left</span></div>
          <div className="flex gap-0.5">
            {item.url && <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => downloadFile(item.url, `konnect_${item.type}_${item.item_id}`)} data-testid={`library-download-${item.item_id}`}><Download className="h-3 w-3" /></Button>}
            {item.url && <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-fuchsia-500 hover:text-fuchsia-700" title="Sell on Marketplace" onClick={handleSell} disabled={listing} data-testid={`library-sell-${item.item_id}`}><Store className="h-3 w-3" /></Button>}
            {driveConnected && !item.saved_to_drive && <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => onSaveDrive(item.item_id)}><Cloud className="h-3 w-3 text-teal-500" /></Button>}
            <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-400 hover:text-red-600" onClick={() => onDelete(item.item_id)}><Trash2 className="h-3 w-3" /></Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
